import {
  Bot,
  BriefcaseBusiness,
  ChevronDown,
  ChevronUp,
  Heart,
  Home,
  MapPin,
  Menu,
  Plus,
  ShoppingCart,
  X,
} from "lucide-react";
import SearchBarV2 from "./searchBarV2";
import Logo from "../assets/logo.svg";
import { useState } from "react";
import { useItems } from "@/hooks/useItems";
import { useDebounce } from "use-debounce";
import { useAuth } from "@/contexts/AuthProvider";
import { useLocation } from "@/contexts/LocationProvider";
import { useNavigate } from "react-router";
import { useItemModal } from "@/contexts/ItemModalProvider";
import { useSearch } from "@/contexts/SearchProvider";
import { useLoginModal } from "@/contexts/LoginModalProvider";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { useCart } from "@/contexts/CartProvider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "./ui/button";
import { Sheet, SheetContent } from "./ui/sheet";
import mealPlaceholder from "@/assets/meal-placeholder.png";
import { useUserDetailModal } from "@/contexts/UserDetailModalProvider";
import { useAddresses } from "@/contexts/AddressesProvider";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export default function Navbar() {
  const { isSearchActive } = useSearch();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebounce(searchQuery, 300);
  const navigate = useNavigate();
  const { data: items } = useItems(debouncedSearch);
  const { isSignedIn, logout } = useAuth();
  const { hasLocation, address } = useLocation();
  const { t, i18n } = useTranslation();
  const { deactivateSearch } = useSearch();
  const { open: openLoginModal } = useLoginModal();
  const { open: openItemModal } = useItemModal();
  const [openProfileDropdown, setOpenProfileDropdown] = useState(false);
  const { itemsCount: cartItemsCount, openCart } = useCart();
  const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);
  const { open: openUserDetailsModal } = useUserDetailModal();
  const { openAddressModal, addresses, setActiveAddress, activeAddress } =
    useAddresses();

  const [addressesPopoverOpen, setAddressesPopoverOpen] = useState(false);

  const onAddressClick = () => {
    openAddressModal({});
  };

  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="h-16 px-4 sm:px-6 md:px-8 lg:px-16 xl:px-24 flex items-center gap-2">
        <div
          className={`h-full items-center gap-2 lg:gap-4 shrink-0 ${
            isSearchActive ? "hidden lg:flex lg:opacity-0" : "flex"
          }`}
        >
          {/* LOGO */}
          <img
            src={Logo}
            alt="Logo"
            className="w-auto lg:h-14 md:h-10 h-8 cursor-pointer shrink-0"
            onClick={() => navigate("/")}
          />

          {/* LOCATION */}

          {/* <div
            className="hidden md:flex items-center gap-4 cursor-pointer"
            onClick={onAddressClick}
          >
            <div className="rounded-full bg-primary-light p-2">
              <MapPin size={16} color="#E78E22" />
            </div>
            <span className="text-primary">
              {hasLocation ? address : t("add_new_address")}
            </span>
            <ChevronDown size={16} color="#E78E22" />
          </div> */}

          <Popover
            open={addressesPopoverOpen}
            onOpenChange={setAddressesPopoverOpen}
          >
            <PopoverTrigger asChild>
              <div
                className="hidden md:flex items-center gap-2 cursor-pointer max-w-xs"
                onClick={() => {
                  if (addresses.length > 1) {
                    setAddressesPopoverOpen(true);
                  } else {
                    openAddressModal({});
                  }
                }}
              >
                <div className="rounded-full bg-primary-light p-2 shrink-0">
                  <MapPin size={16} color="#E78E22" />
                </div>
                <span className="text-primary truncate">
                  {activeAddress ? activeAddress.address : t("add_new_address")}
                </span>
                <ChevronDown size={16} color="#E78E22" className="shrink-0" />
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="grid gap-2">
                {addresses.map((address) => (
                  <div
                    key={address.id}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded-lg p-2"
                    onClick={() => {
                      setActiveAddress(address);
                      setAddressesPopoverOpen(false);
                    }}
                  >
                    <div className="rounded-lg bg-primary-light p-2">
                      {address.label === "home" ? (
                        <Home className="w-4 h-4 text-primary" />
                      ) : address.label === "work" ? (
                        <BriefcaseBusiness className="w-4 h-4 text-primary" />
                      ) : (
                        <MapPin className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <span>{address.address}</span>
                  </div>
                ))}
                <div
                  key="add-new-address"
                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded-lg p-2"
                  onClick={() => {
                    openAddressModal({});
                    setAddressesPopoverOpen(false);
                  }}
                >
                  <div className="rounded-lg bg-primary-light p-2">
                    <Plus className="w-4 h-4 text-primary" />
                  </div>
                  <span>{t("add_new_address")}</span>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* CART - Desktop */}
          <div
            className="relative hidden md:flex items-center justify-center p-2 hover:bg-primary-light rounded-full hover:text-primary transition-all duration-300 ease-in-out cursor-pointer"
            onClick={openCart}
          >
            <ShoppingCart />

            {cartItemsCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {cartItemsCount}
              </span>
            )}
          </div>

          {/* Ai */}
          <div
            className="relative hidden md:flex items-center justify-center p-2 hover:bg-primary-light rounded-full hover:text-primary transition-all duration-300 ease-in-out cursor-pointer"
            onClick={() => navigate("/ai")}
          >
            <Bot />
          </div>
        </div>
        <div className="flex-1 min-w-0 h-full flex items-center">
          {/* SEARCH */}
          <SearchBarV2 value={searchQuery} onChange={setSearchQuery} />
        </div>

        {isSearchActive && (
          <div className="mx-2 md:mx-4 p-2 rounded-full hover:bg-gray-100 transition-all duration-300 ease-in-out cursor-pointer shrink-0">
            <X
              className="h-6 w-6"
              onClick={() => {
                setSearchQuery("");
                deactivateSearch();
              }}
            />
          </div>
        )}

        {/* MOBILE HAMBURGER */}
        <div
          className={`md:hidden flex justify-end gap-2 shrink-0 ${
            isSearchActive ? "hidden" : "flex"
          }`}
        >
          {/* CART - Mobile */}
          <div
            className="relative flex items-center justify-center p-2 hover:bg-primary-light rounded-full hover:text-primary transition-all duration-300 ease-in-out cursor-pointer"
            onClick={openCart}
          >
            <ShoppingCart className="h-5 w-5" />
            {cartItemsCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {cartItemsCount}
              </span>
            )}
          </div>

          {/* FAVOURITES */}
          <div
            className="relative flex items-center justify-center p-2 hover:bg-primary-light rounded-full hover:text-primary transition-all duration-300 ease-in-out cursor-pointer"
            onClick={() => navigate("/favourites")}
          >
            <Heart className="h-5 w-5" />
          </div>

          {/* HAMBURGER MENU BUTTON */}
          <button
            className="p-2 rounded-md hover:bg-gray-100"
            onClick={() => setIsHamburgerOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <Sheet open={isHamburgerOpen} onOpenChange={setIsHamburgerOpen}>
            <SheetContent side="left" className="w-64">
              <div className="flex flex-col gap-4 p-4">
                {/* LOCATION */}
                <div
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={onAddressClick}
                >
                  <MapPin size={16} color="#E78E22" />
                  <span className="text-primary">
                    {hasLocation ? address : t("add_new_address")}
                  </span>
                </div>

                {/* AI ASSISTANT */}
                <div
                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-2 rounded-md"
                  onClick={() => {
                    navigate("/ai");
                    setIsHamburgerOpen(false);
                  }}
                >
                  <Bot size={16} color="#E78E22" />
                  <span className="text-primary">{t("ai_assistant")}</span>
                </div>

                {/* LANGUAGE */}
                <LanguageSwitcher />

                {isSignedIn && (
                  <div className="flex flex-col gap-2">
                    <h3
                      onClick={() => {
                        navigate("/orders");
                        setIsHamburgerOpen(false);
                      }}
                      className="hover:bg-gray-100 p-2 rounded-md"
                    >
                      {t("orders")}
                    </h3>
                    <h3
                      onClick={() => {
                        navigate("/favourites");
                        setIsHamburgerOpen(false);
                      }}
                      className="hover:bg-gray-100 p-2 rounded-md"
                    >
                      {t("favourites")}
                    </h3>
                    <h3
                      onClick={() => {
                        logout();
                        setIsHamburgerOpen(false);
                      }}
                      className="hover:bg-gray-100 p-2 rounded-md"
                    >
                      {t("logout")}
                    </h3>
                  </div>
                )}

                {!isSignedIn && (
                  <div className="mt-16 flex flex-col gap-2">
                    <Button
                      className="w-full"
                      onClick={() =>
                        openLoginModal(() => {
                          setIsHamburgerOpen(false);
                        })
                      }
                    >
                      {t("log in")}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() =>
                        openLoginModal(() => {
                          setIsHamburgerOpen(false);
                        })
                      }
                    >
                      {t("sign up")}
                    </Button>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <div className={`h-full hidden lg:flex items-center gap-4 shrink-0`}>
          {/* PROFILE */}

          {isSignedIn && (
            <DropdownMenu
              open={openProfileDropdown}
              onOpenChange={setOpenProfileDropdown}
              dir={i18n.dir() === "rtl" ? "rtl" : "ltr"}
            >
              <DropdownMenuTrigger asChild>
                <div className="flex items-center cursor-pointer border rounded-full hover:bg-gray-100 transition-all duration-300 ease-in-out p-0.5">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/8/89/Portrait_Placeholder.png"
                    className="h-10 w-10 rounded-full border-2 border-primary cursor-pointer"
                  />
                  <div className="flex items-center justify-center h-10 w-10">
                    {openProfileDropdown ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </div>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48 p-2">
                <DropdownMenuLabel>{t("my_account")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  {t("profile")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/orders")}>
                  {t("my_orders")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/favourites")}>
                  {t("favourites")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => logout()}>
                  {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!isSignedIn && (
            <div className="flex items-center gap-4 justify-end">
              <a
                className="cursor-pointer md:block hidden hover:text-primary"
                onClick={() =>
                  openLoginModal(() => {
                    openUserDetailsModal({ showMissingFieldsOnly: true });
                  })
                }
              >
                {t("log in")}
              </a>
              <a
                className="bg-primary hover:bg-primary/80 text-white px-4 py-2 rounded-full cursor-pointer"
                onClick={() =>
                  openLoginModal(() => {
                    openUserDetailsModal({ showMissingFieldsOnly: true });
                  })
                }
              >
                {t("sign up")}
              </a>
            </div>
          )}

          {/* LANGUAGE */}
          <LanguageSwitcher />
        </div>
      </div>
      {/* Search Results */}
      <div
        className={`min-h-screen sm:min-h-0 bg-white absolute top-16 w-full z-20 border-b border-gray-200 ${
          isSearchActive ? "block" : "hidden"
        }`}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-4 lg:max-w-1/2 lg:mx-auto">
          {items?.slice(0, 4).map((item) => (
            <div
              className="flex gap-2 hover:scale-105 transition-all duration-300 cursor-pointer"
              onClick={() => {
                openItemModal({
                  item,
                  ctaText: "Order",
                  onCtaClick: () => {
                    navigate(`/restaurants/${item.companyId}/items/${item.id}`);
                  },
                });
              }}
            >
              <img
                src={item?.image ?? mealPlaceholder}
                alt="item image"
                className="w-48 h-32 object-cover rounded-md"
              />
              <div className="flex flex-col justify-center">
                <span className="text-sm text-primary font-semibold">
                  ₪{item?.price}
                </span>
                <span className="text-sm font-medium">{item?.name}</span>
                <span className="text-xs text-gray-500">
                  {item.companyName}
                </span>
              </div>
            </div>
          ))}
        </div>

        {items && items.length > 0 && (
          <div className=" gap-2 p-4 lg:max-w-1/2 lg:mx-auto flex justify-center">
            <h1
              className="text-sm text-primary hover:text-primary/80 font-semibold cursor-pointer"
              onClick={() => {
                deactivateSearch();
                navigate(`/search?q=${debouncedSearch}`);
              }}
            >
              {t("view_all_results")}
            </h1>
          </div>
        )}
      </div>
    </div>
  );
}
